function formatMoney(m) {
  if (!m) return '—';
  const amount = Number(m.amount);
  if (Number.isFinite(amount)) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: m.currencyCode || 'USD',
      }).format(amount);
    } catch (_) {
      return `${amount.toFixed(2)} ${m.currencyCode || ''}`.trim();
    }
  }
  return String(m.amount || '—');
}

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString();
}

function normalizeTerritoryCode(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const upper = raw.toUpperCase();
  if (/^[A-Z]{2}$/.test(upper)) return upper;

  const byName = {
    'UNITED KINGDOM': 'GB',
    'GREAT BRITAIN': 'GB',
    'ENGLAND': 'GB',
    'UNITED STATES': 'US',
    'UNITED STATES OF AMERICA': 'US',
    'CANADA': 'CA',
    'AUSTRALIA': 'AU',
    'NEW ZEALAND': 'NZ',
    'IRELAND': 'IE',
    'GERMANY': 'DE',
    'FRANCE': 'FR',
    'SPAIN': 'ES',
    'ITALY': 'IT',
    'PORTUGAL': 'PT',
    'NETHERLANDS': 'NL',
    'BELGIUM': 'BE',
    'SWITZERLAND': 'CH',
    'AUSTRIA': 'AT',
    'SWEDEN': 'SE',
    'NORWAY': 'NO',
    'DENMARK': 'DK',
    'FINLAND': 'FI',
    'POLAND': 'PL',
    'CZECHIA': 'CZ',
    'HUNGARY': 'HU',
    'ROMANIA': 'RO',
    'BULGARIA': 'BG',
    'GREECE': 'GR',
    'CROATIA': 'HR',
    'SLOVENIA': 'SI',
    'SLOVAKIA': 'SK',
    'SERBIA': 'RS',
    'BOSNIA AND HERZEGOVINA': 'BA',
    'MONTENEGRO': 'ME',
    'NORTH MACEDONIA': 'MK',
    'ALBANIA': 'AL',
    'TURKEY': 'TR',
    'UNITED ARAB EMIRATES': 'AE',
    'SAUDI ARABIA': 'SA',
    'INDIA': 'IN',
    'SINGAPORE': 'SG',
    'MALAYSIA': 'MY',
    'THAILAND': 'TH',
    'INDONESIA': 'ID',
    'PHILIPPINES': 'PH',
    'JAPAN': 'JP',
    'SOUTH KOREA': 'KR',
    'HONG KONG': 'HK',
    'TAIWAN': 'TW',
    'SOUTH AFRICA': 'ZA',
    'NIGERIA': 'NG',
    'EGYPT': 'EG',
    'MOROCCO': 'MA',
    'BRAZIL': 'BR',
    'ARGENTINA': 'AR',
    'CHILE': 'CL',
    'COLOMBIA': 'CO',
    'MEXICO': 'MX',
  };

  return byName[upper] || '';
}

function setActiveView(root, view) {
  root.querySelectorAll('[data-ca-view]').forEach((el) => {
    el.hidden = el.dataset.caView !== view;
  });
  root.querySelectorAll('[data-ca-nav]').forEach((a) => {
    const isActive = a.dataset.caNav === view;
    if (isActive) a.setAttribute('aria-current', 'page');
    else a.removeAttribute('aria-current');
  });
}

function getViewFromUrl() {
  const params = new URLSearchParams(window.location.search);
  const tabParam = params.get('tab');
  if (tabParam === 'orders' || tabParam === 'address') return tabParam;
  const legacyViewParam = params.get('view');
  return legacyViewParam === 'orders' || legacyViewParam === 'address' ? legacyViewParam : 'profile';
}

function applyViewFromUrl(root) {
  setActiveView(root, getViewFromUrl());
}

function preservePreviewParamsOnNavLinks(root) {
  const current = new URL(window.location.href);
  const previewThemeId = current.searchParams.get('preview_theme_id');
  const fd = current.searchParams.get('_fd');
  const pb = current.searchParams.get('pb');
  if (!previewThemeId && !fd && !pb) return;

  root.querySelectorAll('a[data-ca-nav]').forEach((link) => {
    const href = link.getAttribute('href');
    if (!href) return;
    const target = new URL(href, window.location.origin);
    if (previewThemeId && !target.searchParams.has('preview_theme_id')) target.searchParams.set('preview_theme_id', previewThemeId);
    if (fd && !target.searchParams.has('_fd')) target.searchParams.set('_fd', fd);
    if (pb && !target.searchParams.has('pb')) target.searchParams.set('pb', pb);
    link.setAttribute('href', `${target.pathname}${target.search}`);
  });
}

function showError(root, message) {
  const err = root.querySelector('[data-ca-error]');
  if (!err) return;
  err.hidden = false;
  err.textContent = message;
}

function hideError(root) {
  const err = root.querySelector('[data-ca-error]');
  if (!err) return;
  err.hidden = true;
  err.textContent = '';
}

function renderOrders(root, orders) {
  const tbody = root.querySelector('[data-ca-orders]');
  const count = root.querySelector('[data-ca-orders-count]');
  if (!tbody) return;

  const list = orders?.nodes || [];
  if (count) count.textContent = list.length ? `${list.length} orders` : 'No orders';

  if (!list.length) {
    tbody.innerHTML = `<tr><td colspan="5" class="ca-muted">You don't have any orders yet.</td></tr>`;
    return;
  }

  tbody.innerHTML = list
    .map((o) => {
      const orderUrl = `/pages/my-account?tab=orders&order=${encodeURIComponent(o.id || '')}`;
      const paymentClass = `ca-badge--${String(o.financialStatus || 'unknown').toLowerCase()}`;
      return `
        <tr>
          <td><a href="${orderUrl}">${o.name || 'Order'}</a></td>
          <td>${formatDate(o.processedAt)}</td>
          <td><span class="ca-badge ${paymentClass}">${o.financialStatus || '—'}</span></td>
          <td class="ca-delivery-text">${o.fulfillmentStatus || '—'}</td>
          <td>${formatMoney(o.totalPrice)}</td>
        </tr>
      `;
    })
    .join('');

  const current = new URL(window.location.href);
  const orderId = current.searchParams.get('order');
  const preview = root.querySelector('[data-ca-order-preview]');
  if (!preview) return;
  if (!orderId) {
    preview.hidden = true;
    return;
  }

  const selectedOrder = list.find((o) => o.id === orderId);
  if (!selectedOrder) {
    preview.hidden = false;
    const nameEl = root.querySelector('[data-ca-preview-name]');
    if (nameEl) nameEl.textContent = 'Order not found.';
    return;
  }

  const setText = (selector, value) => {
    const el = root.querySelector(selector);
    if (el) el.textContent = value;
  };
  setText('[data-ca-preview-name]', selectedOrder.name || 'Order');
  setText('[data-ca-preview-date]', formatDate(selectedOrder.processedAt));
  setText('[data-ca-preview-payment]', selectedOrder.financialStatus || '—');
  setText('[data-ca-preview-delivery]', selectedOrder.fulfillmentStatus || '—');
  setText('[data-ca-preview-total]', formatMoney(selectedOrder.totalPrice));
  preview.hidden = false;
}

function escapeHtml(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function renderAddressList(root, customer) {
  const listWrap = root.querySelector('[data-ca-address-list]');
  if (!listWrap) return;

  const defaultAddressId = customer?.defaultAddress?.id;
  const addresses = customer?.addresses?.nodes || [];
  if (!addresses.length) {
    listWrap.innerHTML = '';
    return;
  }

  listWrap.innerHTML = addresses
    .map((a) => {
      const isDefault = defaultAddressId && a.id === defaultAddressId;
      return `
        <div class="ca-address-item">
          <div class="ca-address-item__head">
            <div>${isDefault ? '<span class="ca-address-default">Default</span>' : ''}</div>
            <div class="ca-address-item__actions">
              <button type="button" class="ca-btn ca-btn--secondary" data-ca-edit-address="${escapeHtml(a.id)}">Edit</button>
              <button type="button" class="ca-btn ca-btn--secondary" data-ca-delete-address="${escapeHtml(a.id)}">Delete</button>
            </div>
          </div>
          <div class="ca-address-item__line">${escapeHtml(a.address1)}</div>
          ${a.address2 ? `<div class="ca-address-item__line">${escapeHtml(a.address2)}</div>` : ''}
          <div class="ca-address-item__line">${escapeHtml([a.city, a.province].filter(Boolean).join(', '))}</div>
          <div class="ca-address-item__line">${escapeHtml([a.zip, a.country].filter(Boolean).join(' '))}</div>
        </div>
      `;
    })
    .join('');
}

window.addEventListener('DOMContentLoaded', async () => {
  const root = document.querySelector('[data-ca-account]');
  if (!root) return;

  if (!window.CustomerAccount.isLoggedIn()) {
    window.location.assign('/pages/login');
    return;
  }

  const storefrontDomain = root.dataset.storefrontDomain;
  const clientId = root.dataset.clientId;
  const logoutRedirect = root.dataset.logoutRedirect || '/';
  const saveBtn = root.querySelector('[data-ca-save-profile]');
  const saveStatus = root.querySelector('[data-ca-save-status]');
  const openAddressFormBtn = root.querySelector('[data-ca-open-address-form]');
  const cancelAddressFormBtn = root.querySelector('[data-ca-cancel-address-form]');
  const addressForm = root.querySelector('[data-ca-address-form]');
  const addressSaveStatus = root.querySelector('[data-ca-address-save-status]');
  const addressIdEl = root.querySelector('[data-ca-address-id]');
  const addressSubmitBtn = root.querySelector('[data-ca-address-submit]');
  const addressFormTitle = root.querySelector('[data-ca-address-form-title]');
  const addressDefaultEl = root.querySelector('[data-ca-address-default]');

  const setAddressStatus = (message, type) => {
    if (!addressSaveStatus) return;
    addressSaveStatus.hidden = !message;
    addressSaveStatus.textContent = message || '';
    addressSaveStatus.classList.remove('ca-alert--error', 'ca-alert--success');
    if (message && type === 'error') addressSaveStatus.classList.add('ca-alert--error');
    if (message && type === 'success') addressSaveStatus.classList.add('ca-alert--success');
  };
  root.querySelectorAll('[data-ca-logout]').forEach((logoutBtn) => {
    logoutBtn.addEventListener('click', async () => {
      await window.CustomerAccount.logoutCustomerAccount({ storefrontDomain, postLogoutRedirectUri: logoutRedirect });
    });
  });

  applyViewFromUrl(root);
  preservePreviewParamsOnNavLinks(root);
  hideError(root);

  root.querySelectorAll('a[data-ca-nav]').forEach((link) => {
    link.addEventListener('click', () => {
      // Some themes use client-side transitions and don't trigger DOMContentLoaded on navigation.
      // Apply the target tab immediately so account views always switch.
      const href = link.getAttribute('href') || '';
      if (href.includes('tab=orders') || href.includes('view=orders')) setActiveView(root, 'orders');
      else if (href.includes('tab=address') || href.includes('view=address')) setActiveView(root, 'address');
      else setActiveView(root, 'profile');
    });
  });

  window.addEventListener('popstate', () => applyViewFromUrl(root));
  window.addEventListener('pageshow', () => applyViewFromUrl(root));

  if (openAddressFormBtn && addressForm) {
    openAddressFormBtn.addEventListener('click', () => {
      addressForm.hidden = false;
      if (addressSubmitBtn) addressSubmitBtn.textContent = 'Save address';
      if (addressFormTitle) addressFormTitle.textContent = 'Add address';
      if (addressIdEl) addressIdEl.value = '';
      if (addressDefaultEl) addressDefaultEl.checked = false;
      setAddressStatus('', null);
    });
  }
  if (cancelAddressFormBtn && addressForm) {
    cancelAddressFormBtn.addEventListener('click', () => {
      addressForm.hidden = true;
      if (addressSubmitBtn) addressSubmitBtn.textContent = 'Save address';
      if (addressFormTitle) addressFormTitle.textContent = 'Add address';
      if (addressIdEl) addressIdEl.value = '';
      if (addressDefaultEl) addressDefaultEl.checked = false;
      setAddressStatus('', null);
    });
  }

  try {
    const customer = await window.CustomerAccount.loadCustomerOverview({ storefrontDomain, clientId });
    if (!customer) {
      window.location.assign('/pages/login');
      return;
    }

    const greeting = root.querySelector('[data-ca-greeting]');
    if (greeting) {
      const name = customer.firstName || customer.lastName ? `${customer.firstName || ''} ${customer.lastName || ''}`.trim() : null;
      greeting.textContent = name ? `Hello, ${name}` : 'Hello';
    }

    const emailEl = root.querySelector('[data-ca-email]');
    const firstNameEl = root.querySelector('[data-ca-first-name]');
    const lastNameEl = root.querySelector('[data-ca-last-name]');
    const phoneEl = root.querySelector('[data-ca-phone]');
    const email = customer?.emailAddress?.emailAddress || '—';
    const phone = customer?.phoneNumber?.phoneNumber || '—';
    if (emailEl) emailEl.value = email;
    if (firstNameEl) firstNameEl.value = customer.firstName || '';
    if (lastNameEl) lastNameEl.value = customer.lastName || '';
    if (phoneEl) phoneEl.value = phone;

    if (saveBtn) {
      saveBtn.addEventListener('click', async () => {
        try {
          if (saveStatus) saveStatus.textContent = 'Saving...';
          saveBtn.disabled = true;
          const updated = await window.CustomerAccount.updateCustomerOverview({
            storefrontDomain,
            clientId,
            firstName: firstNameEl?.value?.trim() || '',
            lastName: lastNameEl?.value?.trim() || '',
          });
          if (updated) {
            if (firstNameEl) firstNameEl.value = updated.firstName || '';
            if (lastNameEl) lastNameEl.value = updated.lastName || '';
            if (phoneEl) phoneEl.value = updated?.phoneNumber?.phoneNumber || '';
          }
          if (saveStatus) saveStatus.textContent = 'Saved.';
          setTimeout(() => {
            if (saveStatus?.textContent === 'Saved.') saveStatus.textContent = '';
          }, 2500);
        } catch (err) {
          if (saveStatus) saveStatus.textContent = err?.message || 'Failed to save.';
        } finally {
          saveBtn.disabled = false;
        }
      });
    }

    const addressEl = root.querySelector('[data-ca-address]');
    if (addressEl) {
      const address = customer.defaultAddress;
      const allAddresses = customer?.addresses?.nodes || [];
      if (!address || allAddresses.length === 0) {
        addressEl.hidden = false;
        addressEl.innerHTML = `
          <p class="ca-muted" style="margin:0;">No delivery address available.</p>
        `;
      } else {
        addressEl.hidden = true;
        const line2 = address.address2 ? `<div>${address.address2}</div>` : '';
        addressEl.innerHTML = `
          <div>${address.address1 || ''}</div>
          ${line2}
          <div>${[address.city, address.province].filter(Boolean).join(', ')}</div>
          <div>${[address.zip, address.country].filter(Boolean).join(' ')}</div>
        `;
      }
    }
    renderAddressList(root, customer);

    renderOrders(root, customer.orders);

    if (addressForm) {
      addressForm.addEventListener('submit', async (event) => {
        event.preventDefault();
        try {
          setAddressStatus('Saving...', null);
          const getValue = (selector) => (root.querySelector(selector)?.value || '').trim();
          const payload = {
            storefrontDomain,
            clientId,
            address1: getValue('[data-ca-address1]'),
            address2: getValue('[data-ca-address2]'),
            city: getValue('[data-ca-city]'),
            zoneCode: getValue('[data-ca-province]'),
            zip: getValue('[data-ca-zip]'),
            territoryCode: normalizeTerritoryCode(getValue('[data-ca-country]')),
          };
          if (!payload.territoryCode) {
            throw new Error('Please select a valid country.');
          }
          if (addressIdEl?.value) {
            await window.CustomerAccount.updateCustomerAddress({
              ...payload,
              addressId: addressIdEl.value,
            });
            if (addressDefaultEl?.checked) {
              await window.CustomerAccount.setDefaultCustomerAddress({
                storefrontDomain,
                clientId,
                addressId: addressIdEl.value,
              });
            }
          } else {
            const created = await window.CustomerAccount.createCustomerAddress(payload);
            if (addressDefaultEl?.checked && created?.id) {
              await window.CustomerAccount.setDefaultCustomerAddress({
                storefrontDomain,
                clientId,
                addressId: created.id,
              });
            }
          }
          setAddressStatus('Address saved.', 'success');
          window.setTimeout(() => window.location.assign('/pages/my-account?tab=address'), 300);
        } catch (err) {
          setAddressStatus(err?.message || 'Failed to save address.', 'error');
        }
      });
    }

    root.querySelectorAll('[data-ca-edit-address]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const id = btn.getAttribute('data-ca-edit-address');
        if (!id || !addressForm) return;
        const source = (customer?.addresses?.nodes || []).find((a) => a.id === id);
        if (!source) return;
        addressForm.hidden = false;
        if (addressIdEl) addressIdEl.value = id;
        if (addressSubmitBtn) addressSubmitBtn.textContent = 'Update address';
        if (addressFormTitle) addressFormTitle.textContent = 'Edit address';
        const setValue = (selector, value) => {
          const el = root.querySelector(selector);
          if (el) el.value = value || '';
        };
        setValue('[data-ca-address1]', source.address1);
        setValue('[data-ca-address2]', source.address2);
        setValue('[data-ca-city]', source.city);
        setValue('[data-ca-province]', source.province);
        setValue('[data-ca-zip]', source.zip);
        setValue('[data-ca-country]', normalizeTerritoryCode(source.country) || source.country);
        if (addressDefaultEl) addressDefaultEl.checked = id === customer?.defaultAddress?.id;
        setAddressStatus('', null);
      });
    });

    root.querySelectorAll('[data-ca-delete-address]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const id = btn.getAttribute('data-ca-delete-address');
        if (!id) return;
        if (!window.confirm('Delete this address?')) return;
        try {
          await window.CustomerAccount.deleteCustomerAddress({
            storefrontDomain,
            clientId,
            customerAddressId: id,
          });
          window.location.assign('/pages/my-account?tab=address');
        } catch (err) {
          setAddressStatus(err?.message || 'Failed to delete address.', 'error');
        }
      });
    });
  } catch (e) {
    showError(root, e?.message || 'Failed to load account.');
  }
});

